/**
 * Product Free Gifts Tier Unlock System
 * Updates tier states based on cart total
 * Supports multiple currencies (AUD/NZD)
 */

class ProductFreeGifts {
  constructor(container) {
    this.container = container;
    this.currency = this.detectCurrency();

    console.log('[ProductFreeGifts] Detected currency:', this.currency);

    this.tiers = Array.from(
      container.querySelectorAll("[data-tier-threshold]")
    ).map((tier) => ({
      element: tier,
      threshold: this.getThresholdForCurrency(tier),
    }));

    console.log('[ProductFreeGifts] Tiers configured:', this.tiers.map(t => ({
      threshold: t.threshold,
      audThreshold: t.element.getAttribute('data-tier-threshold'),
      nzdThreshold: t.element.getAttribute('data-tier-threshold-nzd')
    })));

    this.unlockedClass = "product-free-gifts__tier--unlocked";
    this.lockedClass = "product-free-gifts__tier--locked";

    // Show/hide currency-specific content
    this.updateCurrencyContent();

    this.init();
  }

  detectCurrency() {
    // Try multiple methods to detect currency
    console.log('[ProductFreeGifts] Currency detection sources:', {
      shopifyCurrency: window.Shopify?.currency?.active,
      themeCurrency: window.theme?.currency,
      dataCurrency: document.documentElement.getAttribute("data-currency")
    });

    const currency =
      window.Shopify?.currency?.active ||
      window.theme?.currency ||
      document.documentElement.getAttribute("data-currency") ||
      "AUD";
    return currency.toUpperCase();
  }

  getThresholdForCurrency(tierElement) {
    const audThreshold =
      parseInt(tierElement.getAttribute("data-tier-threshold"), 10) || 0;
    const nzdThreshold = parseInt(
      tierElement.getAttribute("data-tier-threshold-nzd"),
      10
    );

    if (this.currency === "NZD" && nzdThreshold) {
      return nzdThreshold;
    }
    return audThreshold;
  }

  updateCurrencyContent() {
    // Show/hide content based on currency
    console.log('[ProductFreeGifts] Updating currency content for:', this.currency);

    this.tiers.forEach(({ element }, index) => {
      const audContent = element.querySelectorAll('[data-currency="AUD"]');
      const nzdContent = element.querySelectorAll('[data-currency="NZD"]');

      console.log(`[ProductFreeGifts] Tier ${index + 1}:`, {
        audContentCount: audContent.length,
        nzdContentCount: nzdContent.length,
        showingAUD: this.currency === "AUD",
        showingNZD: this.currency === "NZD"
      });

      // Hide first tier entirely for NZD (index 0)
      if (this.currency === "NZD" && index === 0) {
        element.style.display = "none";
        // Also hide the separator after tier 1
        const separator = element.nextElementSibling;
        if (separator && separator.classList.contains('product-free-gifts__separator')) {
          separator.style.setProperty("display", "none", "important");
        }
      } else {
        element.style.display = "";
        const separator = element.nextElementSibling;
        if (separator && separator.classList.contains('product-free-gifts__separator')) {
          separator.style.display = "";
        }
      }

      audContent.forEach((el) => {
        el.style.display = this.currency === "AUD" ? "" : "none";
      });

      nzdContent.forEach((el) => {
        el.style.display = this.currency === "NZD" ? "" : "none";
      });
    });
  }

  init() {
    // Initial cart fetch
    this.updateFromCart();

    // Listen to various cart update events
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Rebuy cart change event (most reliable for Rebuy cart)
    // This event includes the cart data, so we don't need to fetch
    document.addEventListener("rebuy:cart.change", (event) => {
      const total = event?.detail?.cart?.cart?.total_price;
      if (typeof total === "number") {
        console.log('[ProductFreeGifts] Rebuy cart change detected, total:', total);
        this.applyCartTotal(total);
      }
    });

  }

  setTierState(tierElement, unlocked) {
    tierElement.classList.toggle(this.unlockedClass, unlocked);
    tierElement.classList.toggle(this.lockedClass, !unlocked);
    const lockEl = tierElement.querySelector("[data-tier-lock]");
    if (lockEl) {
      lockEl.classList.toggle("is-unlocked", unlocked);
    }
  }

  applyCartTotal(totalCents) {
    console.log('[ProductFreeGifts] Applying cart total:', totalCents);

    if (typeof totalCents !== "number" || isNaN(totalCents)) {
      console.warn('[ProductFreeGifts] Invalid cart total:', totalCents);
      return;
    }

    this.tiers.forEach(({ element, threshold }, index) => {
      const unlocked = totalCents >= threshold;
      console.log(`[ProductFreeGifts] Tier ${index + 1}:`, {
        threshold,
        cartTotal: totalCents,
        unlocked
      });
      this.setTierState(element, unlocked);
    });
  }

  updateFromCart() {
    fetch(`${window.Shopify?.routes?.root || ""}cart.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        console.log('[ProductFreeGifts] Cart fetched:', cart);
        if (cart && typeof cart.total_price === "number") {
          console.log('[ProductFreeGifts] Cart total price:', cart.total_price, 'Currency:', cart.currency);
          this.applyCartTotal(cart.total_price);
        } else {
          console.warn('[ProductFreeGifts] Invalid cart data:', cart);
        }
      })
      .catch((error) => {
        console.error("[ProductFreeGifts] Failed to fetch cart:", error);
      });
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  const container = document.querySelector("[data-product-free-gifts]");
  if (!container) return;

  // Prevent multiple initializations
  if (container.dataset.productFreeGiftsInitialized === "true") return;

  new ProductFreeGifts(container);
  container.dataset.productFreeGiftsInitialized = "true";
});
